// Bounded durable queue of /chunks uploads awaiting a 2xx ack. Backed by
// IndexedDB so it survives full-page navigation (sessionStorage is too small
// and synchronous; IndexedDB is what actually persists chunk bodies). Falls
// back to a bounded in-memory array when IndexedDB is unavailable (e.g. some
// private-browsing modes) -- that fallback does NOT survive navigation, which
// is an accepted degradation, logged once so it's visible in devtools.

import { FLUSH_RETRY_INTERVAL_MS, MAX_CONSECUTIVE_FLUSH_FAILURES, OUTBOX_MAX_BYTES, OUTBOX_MAX_CHUNKS } from './config';
import { endpoints, postBeaconChunk, postJson } from './wire';

const DB_NAME = 'userintuition-recorder';
const DB_VERSION = 1;
const STORE_NAME = 'chunks';
const DB_OPEN_TIMEOUT_MS = 1000;

interface StoredChunk {
  id: number;
  recordingId: string;
  seq: number;
  payload: string; // canonical payload string, JSON.stringify({seq, events}) already applied
  bytes: number;
}

export interface OutboxDeps {
  recordingId: string;
  token: string;
  /** Called once on a 409 from /chunks -- recording is finalized/aborted server-side. */
  onFinalizedOrAborted: () => void;
  /** Called ONCE per failure-streak, when chunk uploads have failed
   * MAX_CONSECUTIVE_FLUSH_FAILURES times in a row. The outbox does NOT stop --
   * it keeps retrying and re-fires this only after a recovery + fresh streak.
   * Treat it as "ingest degraded", not "session over". */
  onPersistentFailure?: () => void;
  /** Runs synchronously immediately before an unload/hidden beacon drain.
   * The recorder uses this to turn its in-memory rrweb tail into a chunk
   * before the outbox snapshots the queue. */
  onBeforeDrainSync?: () => void;
  win?: Window;
  maxQueuedChunks?: number;
  maxQueuedBytes?: number;
}

export class Outbox {
  private queue: StoredChunk[] = [];
  private db: IDBDatabase | null = null;
  private usingIndexedDB = false;
  private nextId = 1;
  /** The currently-running flush() attempt, if any. A concurrent caller
   * (the retry timer, enqueue()'s own fire-and-forget flush, and an explicit
   * flushUntilEmptyOrStuck()) all await this SAME promise instead of one of
   * them getting an early "done" before the network attempt it triggered has
   * actually settled -- that early-return was the bug: flushUntilEmptyOrStuck
   * could resolve before an already-in-flight ack landed. */
  private inFlight: Promise<void> | null = null;
  private stopped = false;
  private consecutiveFailures = 0;
  private persistentFailureNotified = false;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private readonly win: Window;
  private readonly pagehideHandler = () => this.drainSync();
  // iOS Safari fires visibilitychange->hidden reliably when pagehide may not,
  // so we drain on both. This is a drain-only path (identical to pagehide) --
  // it NEVER calls /finalize, /close, or /abort.
  private readonly visibilityHandler = () => {
    if (this.win.document?.visibilityState === 'hidden') this.drainSync();
  };

  constructor(private readonly deps: OutboxDeps) {
    this.win = deps.win ?? window;
  }

  /** Opens IndexedDB (or falls back), loads any unacked chunks for this
   * recording, wires the pagehide + visibilitychange drains, and starts the
   * retry loop. */
  async init(): Promise<void> {
    await this.openDb();
    await this.loadUnacked();
    this.win.addEventListener('pagehide', this.pagehideHandler);
    this.win.document?.addEventListener('visibilitychange', this.visibilityHandler);
    this.flushTimer = setInterval(() => void this.flush(), FLUSH_RETRY_INTERVAL_MS);
    void this.flush();
  }

  /** Enqueue a chunk whose canonical payload has already been computed. */
  enqueue(seq: number, payload: string): void {
    if (this.stopped) return;
    const chunk: StoredChunk = {
      id: this.nextId++,
      recordingId: this.deps.recordingId,
      seq,
      payload,
      bytes: payload.length,
    };
    this.queue.push(chunk);
    this.persist(chunk);
    this.enforceCap();
    void this.flush();
  }

  getPendingCount(): number {
    return this.queue.length;
  }

  isUsingIndexedDB(): boolean {
    return this.usingIndexedDB;
  }

  /** Offer everything still queued to sendBeacon. A `true` return only means
   * the browser accepted the beacon into its own pending queue; it is NOT a
   * server acknowledgement. Keep every chunk in memory + IndexedDB until a
   * normal fetch receives a real 2xx, so the next page can safely retry an
   * unload beacon that the browser later dropped. The backend's seq/hash
   * idempotency makes a retry safe when the beacon did arrive. */
  drainSync(): void {
    this.deps.onBeforeDrainSync?.();
    for (const chunk of [...this.queue]) {
      postBeaconChunk(this.deps.recordingId, this.deps.token, chunk.payload);
    }
  }

  /** Waits for the current queue to drain via normal fetch (used by the
   * explicit finalize flow, which has time to await acks -- unlike pagehide). */
  async flushUntilEmptyOrStuck(): Promise<void> {
    await this.flush();
  }

  stop(): void {
    this.stopped = true;
    if (this.flushTimer) clearInterval(this.flushTimer);
    this.win.removeEventListener('pagehide', this.pagehideHandler);
    this.win.document?.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  /** Wipe all persisted + in-memory state for this recording (used on stop/clear). */
  clearAll(): void {
    this.queue = [];
    if (this.usingIndexedDB && this.db) {
      try {
        const tx = this.db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const req = store.getAll();
        req.onsuccess = () => {
          for (const c of (req.result as StoredChunk[]) || []) {
            if (c.recordingId === this.deps.recordingId) store.delete(c.id);
          }
        };
      } catch {
        // best-effort
      }
    }
  }

  private flush(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    if (this.stopped) return Promise.resolve();
    const run = this.runFlushLoop().finally(() => {
      this.inFlight = null;
    });
    this.inFlight = run;
    return run;
  }

  private async runFlushLoop(): Promise<void> {
    while (this.queue.length > 0) {
      const chunk = this.queue[0];
      if (!chunk) break;
      const result = await postJson(endpoints.chunks(this.deps.recordingId), this.deps.token, chunk.payload);
      if (result.ok) {
        this.consecutiveFailures = 0;
        this.persistentFailureNotified = false; // recovered -- allow a fresh streak to notify again
        this.removeFromQueue(chunk.id);
        this.deletePersisted(chunk.id);
        continue;
      }
      if (result.status === 409) {
        // Recording is finalized/aborted server-side: stop retrying this (and
        // any other queued) chunk immediately, regardless of what the caller's
        // callback does.
        this.stopped = true;
        this.deps.onFinalizedOrAborted();
        return;
      }
      this.consecutiveFailures += 1;
      if (this.consecutiveFailures >= MAX_CONSECUTIVE_FLUSH_FAILURES && !this.persistentFailureNotified) {
        this.persistentFailureNotified = true;
        this.deps.onPersistentFailure?.();
      }
      break; // preserve seq order -- retry this same chunk next tick
    }
  }

  private removeFromQueue(id: number): void {
    this.queue = this.queue.filter((c) => c.id !== id);
  }

  private enforceCap(): void {
    const maxChunks = this.deps.maxQueuedChunks ?? OUTBOX_MAX_CHUNKS;
    const maxBytes = this.deps.maxQueuedBytes ?? OUTBOX_MAX_BYTES;
    let totalBytes = this.queue.reduce((sum, c) => sum + c.bytes, 0);
    while (this.queue.length > maxChunks || totalBytes > maxBytes) {
      const dropped = this.queue.shift();
      if (!dropped) break;
      totalBytes -= dropped.bytes;
      this.deletePersisted(dropped.id);
      // eslint-disable-next-line no-console
      console.warn(
        `[UserIntuitionRecorder] outbox cap exceeded -- dropped chunk seq=${dropped.seq}. ` +
          'The moderator may see a gap in this recording.',
      );
    }
  }

  private persist(chunk: StoredChunk): void {
    if (!this.usingIndexedDB || !this.db) return;
    try {
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(chunk);
    } catch {
      // best-effort; the in-memory queue is still authoritative for this page's lifetime.
    }
  }

  private deletePersisted(id: number): void {
    if (!this.usingIndexedDB || !this.db) return;
    try {
      const tx = this.db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(id);
    } catch {
      // best-effort
    }
  }

  private openDb(): Promise<void> {
    if (typeof indexedDB === 'undefined') {
      this.usingIndexedDB = false;
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        this.usingIndexedDB = ok;
        if (!ok) {
          // eslint-disable-next-line no-console
          console.warn('[UserIntuitionRecorder] IndexedDB unavailable -- falling back to in-memory outbox (will not survive page navigation).');
        }
        resolve();
      };
      const timeout = setTimeout(() => finish(false), DB_OPEN_TIMEOUT_MS);
      try {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          }
        };
        req.onsuccess = () => {
          clearTimeout(timeout);
          this.db = req.result;
          finish(true);
        };
        req.onerror = () => {
          clearTimeout(timeout);
          finish(false);
        };
        req.onblocked = () => {
          clearTimeout(timeout);
          finish(false);
        };
      } catch {
        clearTimeout(timeout);
        finish(false);
      }
    });
  }

  private loadUnacked(): Promise<void> {
    if (!this.usingIndexedDB || !this.db) return Promise.resolve();
    return new Promise((resolve) => {
      try {
        const tx = this.db!.transaction(STORE_NAME, 'readonly');
        const req = tx.objectStore(STORE_NAME).getAll();
        req.onsuccess = () => {
          const all = (req.result as StoredChunk[]) || [];
          this.queue = all
            .filter((c) => c.recordingId === this.deps.recordingId)
            .sort((a, b) => a.seq - b.seq);
          this.nextId = all.reduce((max, c) => Math.max(max, c.id), 0) + 1;
          resolve();
        };
        req.onerror = () => resolve();
      } catch {
        resolve();
      }
    });
  }
}
